// Loan pieces of the sandbox playground: a player's credit score, borrowing
// (quote, then take the loan), and repaying or paying off.
import { useMemo, useState } from "react";
import { Button, Confirm, ErrorNotice, Field, InfoTip, Input, Modal, Money, Panel, Select, useToast } from "../../components/ui";
import { ApiError, type GameApi } from "../../lib/api";
import { newIdempotencyKey } from "../../lib/ids";
import { formatMoney, parseMoney } from "../../lib/money";
import { invalidate, useAction, useQuery } from "../../lib/query";
import type { Account, Bank, Loan, LoanQuote } from "../../lib/types";
import { RATING_LABEL, describeFrequency, loanStatusBadge } from "./loanText";
import { pct } from "./Savings";

/** How each credit-score factor is worked out, for its tooltip. */
const FACTOR_HELP: Record<string, string> = {
  repayment_history: "Instalments paid on time out of all that fell due. Each instalment in arrears costs 10 points, and a default caps this at 5. No loans yet scores 15.",
  income: "5 points for each of the last 4 game weeks with money coming in from someone else, like salaries or sales. Transfers between the player's own accounts don't count.",
  balance_health: "Current and savings balance compared with a typical week's spending. Two weeks covered scores full marks.",
  account_age: "Game days since the player's first transaction. Full marks at 90.",
  spending_discipline: "3 points off for each card payment declined for insufficient funds in the last 4 game weeks.",
  savings: "Savings and fixed deposits compared with weekly income. Four weeks of income saved scores full marks.",
};

const refresh = (gameId: string) => {
  invalidate(`sandbox:${gameId}:`);
};

function scopeNotice(err: unknown) {
  if (err instanceof ApiError && err.code === "insufficient_scope") {
    return <div className="notice warn">This playground key was issued before loans existed. End the session (top right) and start a new one.</div>;
  }
  return null;
}

/** The player's credit score, its parts and how to improve it. */
export function CreditScorePanel({ api, gameId, account }: { api: GameApi; gameId: string; account: Account }) {
  const q = useQuery(account.player_id ? `sandbox:${gameId}:score:${account.player_id}` : null, () => api.creditScore(account.player_id!));
  const s = q.data;
  return (
    <Panel title={<>Credit score <InfoTip>Worked out by the engine from this player's money history in your game. Loan products use it to decide who may borrow, how much, and at what rate.</InfoTip></>}
      sub={s ? `As of game day ${s.as_of_game_day}${s.thin_file ? " · no loans yet" : ""}` : undefined}>
      {scopeNotice(q.error) ?? <ErrorNotice error={q.error} onRetry={q.reload} />}
      {s && (
        <div className="score">
          <div className={`score-dial rating-${s.rating}`} aria-label={`Score ${s.score} out of 100`}>
            <strong>{s.score}</strong>
            <span>{RATING_LABEL[s.rating]}</span>
          </div>
          <div className="grow stack" style={{ gap: 10 }}>
            <ul className="score-factors">
              {s.factors.map((f) => (
                <li key={f.factor}>
                  <span>{f.name} <InfoTip label={`About ${f.name.toLowerCase()}`}>{FACTOR_HELP[f.factor] ?? ""} Now: {f.detail}.</InfoTip></span>
                  <span className="mix-bar"><i style={{ width: `${(f.points / f.max) * 100}%` }} /></span>
                  <span className="num">{f.points}/{f.max}</span>
                </li>
              ))}
            </ul>
            {s.tips.length > 0 && (
              <ul className="small muted" style={{ margin: 0, paddingLeft: 18 }}>
                {s.tips.slice(0, 3).map((t) => <li key={t}>{t}</li>)}
              </ul>
            )}
          </div>
        </div>
      )}
    </Panel>
  );
}

/** Loans repaid from this account. */
export function LoansPanel({ api, gameId, account }: { api: GameApi; gameId: string; account: Account }) {
  const toast = useToast();
  const q = useQuery(`sandbox:${gameId}:loans:${account.id}`, () => api.loans({ account_id: account.id }));
  const [open, setOpen] = useState<string | null>(null);
  const [repaying, setRepaying] = useState<Loan | null>(null);
  const [payingOff, setPayingOff] = useState<Loan | null>(null);
  const rows = q.data?.data ?? [];
  if (q.error && !(q.error instanceof ApiError && q.error.code === "insufficient_scope")) return null;
  if (!q.data || rows.length === 0) return null;
  const cur = account.currency;
  return (
    <Panel title="Loans" sub="Repaid from this account. Instalments are collected automatically on their due day; advance the clock to watch." flush>
      <div className="table-wrap">
        <table className="data">
          <thead><tr>
            <th>Loan</th>
            <th className="num">Left to pay <InfoTip>Everything still owed: unpaid principal, interest and late fees. "Overdue" is the part already past due.</InfoTip></th>
            <th>Next <InfoTip>The next instalment's due game day and amount, collected automatically from this account. Use +7 days to jump ahead.</InfoTip></th>
            <th>Status</th><th />
          </tr></thead>
          <tbody>
            {rows.map((l) => (
              <LoanRow key={l.id} api={api} gameId={gameId} loan={l} cur={cur} open={open === l.id} onToggle={() => setOpen(open === l.id ? null : l.id)}
                onRepay={() => setRepaying(l)} onPayoff={() => setPayingOff(l)} />
            ))}
          </tbody>
        </table>
      </div>
      {repaying && <Repay api={api} gameId={gameId} loan={repaying} account={account} onClose={() => setRepaying(null)} onDone={() => { setRepaying(null); toast("Repayment made"); }} />}
      {payingOff && <Payoff api={api} gameId={gameId} loan={payingOff} account={account} onClose={() => setPayingOff(null)} onDone={() => { setPayingOff(null); toast("Loan paid off"); }} />}
    </Panel>
  );
}

function LoanRow({ api, gameId, loan: l, cur, open, onToggle, onRepay, onPayoff }: {
  api: GameApi; gameId: string; loan: Loan; cur: Account["currency"]; open: boolean; onToggle: () => void; onRepay: () => void; onPayoff: () => void;
}) {
  const detail = useQuery(open ? `sandbox:${gameId}:loan:${l.id}` : null, () => api.loan(l.id));
  const active = l.status === "active";
  return (
    <>
      <tr className="clickable" onClick={onToggle}>
        <td>
          <div className="cell-title">{l.product_name}</div>
          <div className="cell-sub">{formatMoney(l.principal_minor, cur)} at {pct(l.rate_bps)} · {l.installments_count} {describeFrequency(l.repayment_frequency)} · score {l.score_used}</div>
        </td>
        <td className="num">
          <Money minor={l.remaining_minor} currency={cur} />
          {l.arrears_minor > 0 && <div className="cell-sub" style={{ color: "var(--debit)" }}>{formatMoney(l.arrears_minor, cur)} overdue</div>}
        </td>
        <td>{active && l.next_due_day ? <>{l.next_due_day}<div className="cell-sub">{formatMoney(l.next_due_minor, cur)}</div></> : <span className="muted">—</span>}</td>
        <td>{loanStatusBadge(l)}</td>
        <td className="num" onClick={(e) => e.stopPropagation()}>
          {active && (
            <span className="row" style={{ gap: 6, justifyContent: "flex-end" }}>
              <Button size="small" onClick={onRepay}>Repay</Button>
              <Button size="small" onClick={onPayoff}>Pay off</Button>
            </span>
          )}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5} style={{ background: "var(--surface-2)" }}>
            <ErrorNotice error={detail.error} />
            {detail.data?.installments && (
              <table className="data compact">
                <thead><tr><th>#</th><th>Due</th><th className="num">Principal</th><th className="num">Interest</th><th className="num">Late fee</th><th className="num">Owed</th><th>Status</th></tr></thead>
                <tbody>
                  {detail.data.installments.map((i) => (
                    <tr key={i.seq}>
                      <td>{i.seq}</td>
                      <td>{i.due_day}</td>
                      <td className="num">{formatMoney(i.principal_due_minor, cur)}</td>
                      <td className="num">{formatMoney(i.interest_due_minor, cur)}</td>
                      <td className="num">{i.late_fee_due_minor ? formatMoney(i.late_fee_due_minor, cur) : "—"}</td>
                      <td className="num">{i.owed_minor ? formatMoney(i.owed_minor, cur) : "—"}</td>
                      <td><span className={`badge ${i.status === "paid" ? "ok" : i.status === "missed" ? "bad" : "plain"}`}>{i.status}{i.missed && i.status === "paid" ? " late" : ""}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function Repay({ api, gameId, loan, account, onClose, onDone }: { api: GameApi; gameId: string; loan: Loan; account: Account; onClose: () => void; onDone: () => void }) {
  const cur = account.currency;
  const [amount, setAmount] = useState(String((loan.arrears_minor || loan.next_due_minor) / 10 ** cur.exponent));
  const key = useMemo(() => newIdempotencyKey("repay"), []);
  const minor = parseMoney(amount, cur.exponent);
  const pay = useAction(() => api.repayLoan(loan.id, minor ?? 0, key));
  return (
    <Modal open title={`Repay ${loan.product_name}`} onClose={onClose}
      description={<>Missed instalments are paid first, then the next ones in order. {formatMoney(loan.remaining_minor, cur)} is left to pay.</>}
      onSubmit={async (e) => { e.preventDefault(); if (await pay.run()) { refresh(gameId); onDone(); } }}
      footer={<><Button onClick={onClose}>Cancel</Button><Button type="submit" variant="primary" busy={pay.pending} disabled={!minor}>Repay</Button></>}>
      <div className="form">
        <Field label="Amount" hint={`From ${account.account_name}, which has ${formatMoney(account.available_balance, cur)} available.`}
          tip="Missed instalments are paid first, then upcoming ones in order. Repaying early doesn't reduce interest; use Pay off to save it.">
          <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus required />
        </Field>
        <ErrorNotice error={pay.error} />
      </div>
    </Modal>
  );
}

function Payoff({ api, gameId, loan, account, onClose, onDone }: { api: GameApi; gameId: string; loan: Loan; account: Account; onClose: () => void; onDone: () => void }) {
  const cur = account.currency;
  const quote = useQuery(`sandbox:${gameId}:payoff:${loan.id}`, () => api.payoffQuote(loan.id));
  const key = useMemo(() => newIdempotencyKey("payoff"), []);
  const pay = useAction(() => api.payoffLoan(loan.id, quote.data!.total_minor, key));
  const qd = quote.data;
  return (
    <Confirm open title={`Pay off ${loan.product_name}`} confirmLabel={qd ? `Pay ${formatMoney(qd.total_minor, cur)}` : "Pay off"} busy={pay.pending}
      disabled={!qd} error={pay.error ?? quote.error} onClose={onClose}
      onConfirm={async () => { if (await pay.run()) { refresh(gameId); onDone(); } }}
      body={qd ? (
        <dl className="facts">
          {qd.arrears_minor > 0 && <><dt>Overdue now</dt><dd>{formatMoney(qd.arrears_minor, cur)}</dd></>}
          <dt>Principal left</dt><dd>{formatMoney(qd.principal_minor, cur)}</dd>
          <dt>Interest (this period) <InfoTip>Interest for the period you're in is still charged in full.</InfoTip></dt><dd>{formatMoney(qd.interest_minor, cur)}</dd>
          {qd.late_fees_minor > 0 && <><dt>Late fees</dt><dd>{formatMoney(qd.late_fees_minor, cur)}</dd></>}
          <dt>Interest saved <InfoTip>Interest for later periods that you won't pay because you're settling early.</InfoTip></dt><dd style={{ color: "var(--credit)" }}>{formatMoney(qd.interest_saved_minor, cur)}</dd>
        </dl>
      ) : "Working out the payoff…"} />
  );
}

/** Borrow: choose a product, see the quote, take the loan. */
export function ApplyLoan({ api, gameId, account, bank, onClose }: { api: GameApi; gameId: string; account: Account; bank: Bank; onClose: () => void }) {
  const toast = useToast();
  const cur = account.currency;
  const products = useQuery(`sandbox:${gameId}:loan-products:${bank.id}`, () => api.loanProducts(bank.id));
  const live = (products.data?.data ?? []).filter((p) => p.status === "active");
  const [productId, setProductId] = useState("");
  const product = live.find((p) => p.id === productId) ?? live[0];
  const [amount, setAmount] = useState("");
  const [term, setTerm] = useState(0);
  const [external, setExternal] = useState("");
  const [quote, setQuote] = useState<LoanQuote | null>(null);
  const key = useMemo(() => newIdempotencyKey("loan"), []);
  const termDays = term || product?.terms[0]?.days || 0;
  const needsExternal = product && product.score_source !== "engine";
  const body = () => ({
    account_id: account.id, product_id: product!.id, amount_minor: parseMoney(amount, cur.exponent) ?? 0, term_days: termDays,
    ...(needsExternal ? { external_score: Number(external) } : {}),
  });
  const getQuote = useAction(() => api.quoteLoan(body()));
  const take = useAction(() => api.applyLoan({ ...body(), max_rate_bps: quote!.rate_bps }, key));

  if (products.data && live.length === 0) {
    return (
      <Modal open title="Borrow" onClose={onClose} footer={<Button onClick={onClose}>Close</Button>}>
        <p className="muted">{bank.name} has no loan products yet. Add one in the Loans tab, and turn loans on for the bank.</p>
      </Modal>
    );
  }
  const reset = () => setQuote(null);
  return (
    <Modal open size="wide" title={`Borrow into ${account.account_name}`} onClose={onClose}
      description="The money lands in this account, and instalments are collected from it on the game clock."
      onSubmit={async (e) => {
        e.preventDefault();
        if (!quote) { const q = await getQuote.run(); if (q) setQuote(q); return; }
        if (await take.run()) { refresh(gameId); toast("Loan paid in"); onClose(); }
      }}
      footer={<>
        <Button onClick={onClose}>Cancel</Button>
        {quote && <Button onClick={reset}>Change</Button>}
        <Button type="submit" variant="primary" busy={getQuote.pending || take.pending} disabled={!product || !parseMoney(amount, cur.exponent) || (quote ? !quote.eligible : false)}>
          {quote ? `Take ${formatMoney(quote.amount_minor, cur)}` : "Get a quote"}
        </Button>
      </>}>
      <div className="form">
        <div className="form-row">
          <Field label="Loan" tip="The bank's loan products. Set them up, with their rates and rules, in the Loans tab.">
            <Select value={product?.id ?? ""} onChange={(e) => { setProductId(e.target.value); setTerm(0); reset(); }}>
              {live.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </Field>
          <Field label="Amount" hint={product ? `${formatMoney(product.min_principal_minor, cur)} to ${formatMoney(product.max_principal_minor, cur)}` : undefined}
            tip="How much to borrow. Any opening fee comes out of it, so slightly less lands in the account. The player's score may allow less than the product's maximum.">
            <Input inputMode="decimal" value={amount} onChange={(e) => { setAmount(e.target.value); reset(); }} autoFocus required placeholder="50000" />
          </Field>
          <Field label="Term" tip="How long to repay, in game days. Longer terms mean smaller instalments but more interest in total.">
            <Select value={termDays} onChange={(e) => { setTerm(Number(e.target.value)); reset(); }}>
              {product?.terms.map((t) => <option key={t.days} value={t.days}>{t.days} game days · {pct(t.rate_bps)} a year</option>)}
            </Select>
          </Field>
        </div>
        {needsExternal && (
          <Field label="Game's credit score" tip="Your game's own trust or reputation score, 0 to 100. A blend product mixes it with the engine's score." hint="This product uses your game's own score (0–100). In your game, your server sends it with the application.">
            <Input type="number" min={0} max={100} value={external} onChange={(e) => { setExternal(e.target.value); reset(); }} required style={{ maxWidth: 160 }} />
          </Field>
        )}
        <ErrorNotice error={getQuote.error ?? take.error} />
        {quote && <QuoteView quote={quote} cur={cur} />}
      </div>
    </Modal>
  );
}

function QuoteView({ quote: q, cur }: { quote: LoanQuote; cur: Account["currency"] }) {
  return (
    <div className="stack" style={{ gap: 12 }}>
      {q.eligible ? (
        <div className="notice"><strong>Approved.</strong> Credit score {q.score.score} ({RATING_LABEL[q.score.rating as keyof typeof RATING_LABEL] ?? q.score.rating}) gets {pct(q.rate_bps)} a year{q.rate_bps !== q.base_rate_bps ? ` (base ${pct(q.base_rate_bps)})` : ""}.</div>
      ) : (
        <div className="notice error" role="alert">
          <strong>Not approved.</strong> {q.reasons.map((r) => r.message).join(" ")}
          {q.max_principal_minor > 0 && <> This player can borrow up to {formatMoney(q.max_principal_minor, cur)}.</>}
        </div>
      )}
      <dl className="facts">
        <dt>Paid into the account <InfoTip>The loan amount minus any opening fee.</InfoTip></dt><dd>{formatMoney(q.net_disbursement_minor, cur)}{q.origination_fee_minor > 0 && <span className="cell-sub"> after a {formatMoney(q.origination_fee_minor, cur)} fee</span>}</dd>
        <dt>Instalments <InfoTip>Collected automatically from this account on each due game day. With equal (amortized) instalments, the last one may differ by a little rounding.</InfoTip></dt><dd>{q.installments_count} {describeFrequency(q.repayment_frequency)} of about {formatMoney(q.installment_minor, cur)}</dd>
        <dt>Total interest <InfoTip>What borrowing costs if every instalment is paid on time. Paying off early waives later periods' interest; late fees are extra.</InfoTip></dt><dd>{formatMoney(q.total_interest_minor, cur)}</dd>
        <dt>Total to repay <InfoTip>The amount borrowed plus interest.</InfoTip></dt><dd><strong>{formatMoney(q.total_repayable_minor, cur)}</strong></dd>
      </dl>
    </div>
  );
}
